import { TZDateMini } from '@date-fns/tz';
import { isAfter, isBefore, isValid } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useDateTimeFormat } from './useDateTimeFormat.js';
import { useSpinButtons } from './useSpinButtons.js';
import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { usePrevious } from '../../../core/hooks/usePrevious.js';
import type { TimeValue } from '../../../core/types/time.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getTimeValue,
  removePrecision,
} from '../../forms-core/time/timeParser.js';
import type {
  DateSelectorProps,
  DateTimePickerProps,
  DateTimePickerType,
} from '../types.js';
import { focusElementWithRetry } from '../utils/focusDateTimeInput.js';

/**
 * Hook to provide the validation and 2way binding between the iso date and the rendered spinbuttons.
 */
export function useDateTimePicker(
  // 'type' may be 'never', but for the hook we always want to accept it.
  props: Omit<DateTimePickerProps, 'type'> & {
    type?: DateTimePickerType;
    valueType?: TimeValue['type'];
    value: string | null;
  },
) {
  const {
    type,
    precision,
    min,
    max,
    disabled = false,
    readOnly = false,
    value: propValue,
    onChange,
    required,
    valueType,
  } = props;

  // The spin buttons are only visible and interactive
  // in 'iso8601' mode and should be disabled for expression input
  const isDisabled = disabled || valueType !== 'iso8601';

  const timezone = getTimezone();

  // use the aria label of the datetime picker to prefix each spinbutton aria label
  const { 'aria-label': ariaLabelPrefix } = useAriaLabelingProps(props);

  const value = propValue === '' ? null : propValue;

  const [isoDateTime, setIsoDateTime] = useState(
    getTimeValue(value ?? undefined, precision)?.absoluteDate ?? null,
  );

  // Values we dispatched to onChange but the parent hasn't echoed back yet.
  // The controlled-value effect uses this set to skip echoes of our own
  // dispatches, which would otherwise reset the spin button cursor.
  const dispatchedValuesRef = useRef(new Set<string | null>([value]));
  // Newest dispatched value; its echo marks the round-trip as settled.
  const latestDispatchedValueRef = useRef<string | null>(value);
  // Last ISO key sent to onChange. Dedupes back-to-back dispatches when
  // dateTime is unchanged (INVALID_DATE singleton) and keeps the sync
  // keyDown path aligned with the passive effect.
  const lastDispatchedIsoRef = useRef<string | null>(
    getTimeValue(value ?? undefined, precision)?.absoluteDate ?? null,
  );
  const spinButtonChangeHandledRef = useRef(false);
  // Set to true by updatePart when a clear (partValue === '') occurs; ensures
  // the passive effect runs even when a prior sync dispatch already set
  // spinButtonChangeHandledRef, so bulk "non-empty + clear" batches don't lose
  // the final null/'Invalid Date' onChange.
  const hasPendingClearRef = useRef(false);
  const { toParts, emptyParts } = useDateTimeFormat(type, precision);
  const {
    state: { isValid: isValidFormControl },
  } = useFormFieldMessagesContext();
  const dispatchDateTimeChange = useCallback(
    (newDateTime: Date | null | undefined) => {
      spinButtonChangeHandledRef.current = true;

      let newKey: string | null;
      if (!newDateTime) {
        newKey = null;
      } else if (!isValid(newDateTime)) {
        newKey = 'Invalid Date';
      } else {
        newKey = newDateTime.toISOString();
      }

      if (lastDispatchedIsoRef.current === newKey) {
        spinButtonChangeHandledRef.current = false;
        return;
      }
      lastDispatchedIsoRef.current = newKey;

      const dispatched = dispatchedValuesRef.current;
      if (newKey === null) {
        dispatched.add(null);
        latestDispatchedValueRef.current = null;
        setIsoDateTime(null);
        onChange?.(null);
      } else if (newKey === 'Invalid Date') {
        dispatched.add('Invalid Date');
        latestDispatchedValueRef.current = 'Invalid Date';
        setIsoDateTime(null);
        onChange?.('Invalid Date');
      } else {
        const precisionMode = type === 'date' ? 'day' : precision;
        const dispatchedValue = removePrecision(newKey, precisionMode);
        dispatched.add(dispatchedValue);
        latestDispatchedValueRef.current = dispatchedValue;
        setIsoDateTime(newKey);
        onChange?.(getTimeValue(newKey, precision) ?? null);
      }

      // Defensive size cap: evict the oldest entry if the set grows beyond the
      // limit. This bounds memory usage when the parent permanently drops echoes
      // for the latest dispatch (which is the only trigger for bulk-clear).
      // Evicting an orphaned entry is safe in practice — it just means a later
      // external write matching that value will re-apply rather than be skipped.
      // JavaScript Set preserves insertion order so values().next() is oldest.
      while (dispatched.size > 20) {
        const oldest = dispatched.values().next();
        if (!oldest.done) {
          dispatched.delete(oldest.value);
        } else {
          break;
        }
      }
    },
    [onChange, precision, type],
  );

  const onPartClear = useCallback(() => {
    hasPendingClearRef.current = true;
  }, []);

  const { spinButtons, dateTime, updateParts, handlers, parts, toDateTime } =
    useSpinButtons(
      isoDateTime,
      type,
      precision,
      isDisabled,
      readOnly,
      ariaLabelPrefix,
      value,
      dispatchDateTimeChange,
      onPartClear,
    );
  const prevDateTime = usePrevious(dateTime);

  const minDateTime = useMemo(() => {
    const candidate =
      min !== undefined ? new TZDateMini(min, timezone) : undefined;
    return isValid(candidate) ? candidate : undefined;
  }, [min, timezone]);
  const maxDateTime = useMemo(() => {
    const candidate =
      max !== undefined ? new TZDateMini(max, timezone) : undefined;
    return isValid(candidate) ? candidate : undefined;
  }, [max, timezone]);

  /**
   * Callback to apply a given iso date to the currently rendered spinbuttons
   * Accepts a pass filter to apply the iso datetime to only a subset of the spinbuttons.
   * Returns true, if the iso date was applied successfully. False otherwise.
   */
  const applyDateTime = useCallback(
    (
      isoDateTime: string | null,
      passFilter?: Set<Intl.DateTimeFormatPart['type']>,
    ) => {
      if (isoDateTime === null) {
        updateParts(emptyParts);
        return true;
      }

      const newDate = new TZDateMini(isoDateTime, timezone);

      // Add timezone offset for type 'date' to have the correct date when there is no time information (2025-01-01Z).
      newDate.setMinutes(
        newDate.getMinutes() +
          (type === 'date' ? newDate.getTimezoneOffset() : 0),
      );

      if (!isValid(newDate)) {
        return false;
      }
      const newDateParts = toParts(newDate);
      passFilter
        ? updateParts(newDateParts.filter((p) => passFilter.has(p.type)))
        : updateParts(newDateParts);

      return true;
    },
    [emptyParts, timezone, toParts, type, updateParts],
  );

  const dateSelectorProps: DateSelectorProps = useMemo(
    () => ({
      value: isoDateTime ?? TZDateMini.tz(timezone).toISOString(),
      onChange: (newIsoString: string) => {
        // Only apply the date part of the iso date returned by the calendar.
        // Don't touch the time values, but set the focus to the hour field in the next render cycle.
        applyDateTime(newIsoString, new Set(['year', 'month', 'day']));
        // Selecting a date in the calendar closes the overlay and restores the focus to the date picker trigger.
        // In order to set the focus to the time spin buttons, we need to defer setting the focus.
        focusElementWithRetry(() => handlers.get('hour')?.element.current);
      },
      disabled: disabled || readOnly,
      error: false,
    }),
    [isoDateTime, timezone, disabled, readOnly, applyDateTime, handlers],
  );

  // Show the error state, if the value is outside of given bounds or the date object is invalid.
  const error = useMemo(() => {
    if (!isValidFormControl) {
      return true;
    }

    if (!required && isoDateTime === null) {
      return false;
    }

    return (
      (dateTime === null ||
        !isValid(dateTime) ||
        (minDateTime && isBefore(dateTime, minDateTime)) ||
        (maxDateTime && isAfter(dateTime, maxDateTime))) ??
      false
    );
  }, [
    isValidFormControl,
    required,
    isoDateTime,
    dateTime,
    minDateTime,
    maxDateTime,
  ]);

  // true, if none of the content parts contains any content
  const empty = useMemo(
    () => !parts.some((part) => part.type !== 'literal' && part.value !== ''),
    [parts],
  );

  // Dispatches dateTime changes from non-spinbutton paths (calendar,
  // applyDateTime). The `handled` flag skips this effect when the sync
  // keyDown path already dispatched; the numeric guards drop no-op re-fires
  // when the memo returns a new reference for the same value.
  // `clearPending` overrides `handled`: if a clear happened in the same batch
  // as a sync dispatch (e.g. Backspace on a filled field), the effect must
  // still run to coalesce and dispatch the resulting null/'Invalid Date'.
  useEffect(() => {
    const handled = spinButtonChangeHandledRef.current;
    spinButtonChangeHandledRef.current = false;
    const clearPending = hasPendingClearRef.current;
    hasPendingClearRef.current = false;
    if (handled && !clearPending) {
      return;
    }
    const numericDateTime = +(dateTime || 0);
    const numericPrevDateTime = +(prevDateTime || 0);
    if (
      prevDateTime === undefined ||
      (Number.isNaN(numericDateTime) && Number.isNaN(numericPrevDateTime)) ||
      numericDateTime === numericPrevDateTime
    ) {
      return;
    }
    dispatchDateTimeChange(dateTime);
  });

  // If the controlled value has changed, apply the new value to the spinbuttons.
  // Don't combine with the above effect, this needs to run in the next render cycle.
  //
  // Known limitation (out-of-order echoes): if the parent echoes the latest dispatch
  // before an earlier one (e.g. async state updates delivered out of order), the
  // dispatched.clear() below fires early and discards the still-pending earlier entry.
  // The earlier echo then falls through to applyDateTime, momentarily re-applying a
  // stale intermediate value. In practice this requires out-of-order controlled-prop
  // updates which React's batching makes unlikely; a generation-counter approach would
  // eliminate this but is not warranted at the current risk level.
  useEffect(() => {
    const dispatched = dispatchedValuesRef.current;

    if (dispatched.has(value)) {
      // Echo of our newest dispatch: clear the whole Set so entries the
      // parent coalesced away can't linger and later block a legitimate
      // external write. Stale (non-latest) echoes only remove their own entry.
      if (value === latestDispatchedValueRef.current) {
        dispatched.clear();
      } else {
        dispatched.delete(value);
      }
      return;
    }

    const appliedIso =
      getTimeValue(value ?? undefined, precision)?.absoluteDate ?? null;
    applyDateTime(appliedIso);
    // Reset so a subsequent user edit to the same value isn't silently dropped
    // by the dedup guard in dispatchDateTimeChange.
    lastDispatchedIsoRef.current = appliedIso;

    // Only run if the controlled prop value is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return {
    isoDateTime,
    spinButtons,
    handlers,
    dateSelectorProps,
    error,
    empty,
    applyDateTime,
    firstSpinButtonHandler: handlers.get(parts[0]?.type),
    dateTime,
    toDateTime,
    parts,
  };
}
